import get from 'lodash/get';
import { action, autorun, computed, makeObservable, observable, type ObservableMap } from 'mobx';

import { SOCKET_USER_LOYALTY_UPDATE } from '../../config/constants';
import { LoyaltyTutorialSteps } from '../../features/loyaltyProgramTutorial/constants';
import { isLoyaltyProgramTutorialFlagOn } from '../../hooks/loyalty-program/helper';
import { analytics } from '../../services/analytics';
import { getRequest, postRequest } from '../../services/api';
import { identifyUser, ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import { devError } from '../../utils/log';
import type { IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';

import { type LoyaltyTierKey, type Tier, type UserLoyalty, type UserLoyaltySocketMessage } from './types';

export class LoyaltyProgramStore implements IFeatureManagerStore<'LoyaltyProgram'> {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    const subscription = this.subscribeToSocket.bind(this);

    this.updateLoyaltyProgramFlag(resolveFlag('loyalty_program', true));

    ldClient?.on('ready', () => {
      const loyaltyProgramFlag = resolveFlag('loyalty_program', true);
      this.updateLoyaltyProgramFlag(loyaltyProgramFlag);

      const loyaltyTutorialFlag = resolveFlag('feat_loyalty_program_tutorial', true);
      const isLoyaltyTutorialFlagEnabled = isLoyaltyProgramTutorialFlagOn(loyaltyTutorialFlag);
      this.updateLoyaltyProgramTutorialFlag(isLoyaltyTutorialFlagEnabled);
    });

    ldClient?.on('change', (changes) => {
      if ('loyalty_program' in changes) {
        this.updateLoyaltyProgramFlag(changes.loyalty_program.current);
      }

      if ('feat_loyalty_program_tutorial' in changes) {
        const isEnabled = isLoyaltyProgramTutorialFlagOn(changes.feat_loyalty_program_tutorial.current);
        this.updateLoyaltyProgramTutorialFlag(isEnabled);
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;

      if (isUserAuthorized && this.loyaltyProgramFlag) {
        this.resolveAndUpdateFeatureFlagTutorial();
        socket.on(SOCKET_USER_LOYALTY_UPDATE, subscription);
      } else {
        this.updateLoyaltyProgramTutorialFlag(false);
        socket.off(SOCKET_USER_LOYALTY_UPDATE, subscription);
      }
    });
  }

  dialogTypes = ['LoyaltyProgram'] as const;

  async fetch() {
    if (!this.loyaltyProgramFlag) {
      return 'OFF';
    }

    try {
      await this.fetchUserLoyalty();
      await this.fetchAllTiers();

      return 'READY';
    } catch {
      return 'ERROR';
    }
  }

  resolveAndUpdateFeatureFlagTutorial() {
    const loyaltyTutorialFlag = resolveFlag('feat_loyalty_program_tutorial', true);
    const isLoyaltyTutorialFlagEnabled = isLoyaltyProgramTutorialFlagOn(loyaltyTutorialFlag);
    this.updateLoyaltyProgramTutorialFlag(isLoyaltyTutorialFlagEnabled);
  }

  startDialogs() {
    const { finishedLoyaltyTutorial } = this.rootStore.userStore.profile ?? {};

    this.setCompletedTierBonusEligibilityCheck(true);

    if (!finishedLoyaltyTutorial && this.loyaltyProgramTutorialFlag) {
      this.startTutorial();
      return true;
    }

    if (!this.userLoyalty) {
      return false;
    }

    const isCurrentTier = this.userLoyalty.currentTier.UserTier?.isCurrentTier;
    const isEntryTier = this.userLoyalty.currentTier.level === 1;
    const claimedEntryBonus = !!this.userLoyalty.currentTier.UserTier?.entryDate;
    const { isEligibleForMonthlyBonus, isEligibleForBirthdayGift } = this.userLoyalty;

    if (isCurrentTier && !isEntryTier && !claimedEntryBonus) {
      this.rootStore.uiStore.setOpenFullPageDialog('new_tier_congrats');
      return true;
    }

    if (isCurrentTier && isEligibleForMonthlyBonus) {
      this.rootStore.uiStore.setOpenFullPageDialog('tier_monthly_bonus');
      return true;
    }

    if (isCurrentTier && isEligibleForBirthdayGift) {
      this.rootStore.uiStore.setOpenFullPageDialog('tier_birthday_gift');
      return true;
    }

    return false;
  }

  @observable
  public loyaltyProgramFlag: boolean = false;

  @observable
  public loyaltyProgramTutorialFlag: boolean = false;

  @action
  updateLoyaltyProgramFlag(value: boolean) {
    this.loyaltyProgramFlag = value;
  }

  @action
  updateLoyaltyProgramTutorialFlag(value: boolean) {
    this.loyaltyProgramTutorialFlag = value;
  }

  @observable
  public loyaltyProgramTutorial = {
    step: LoyaltyTutorialSteps.CHECK_CURRENT_STATUS,
    open: false,
  };

  @observable
  public completedTierBonusEligibilityCheck: boolean = false;

  @observable
  public loading: ObservableMap<string, boolean> = observable.map([]);

  @observable
  public allTiers?: Tier[];

  @observable
  public userLoyalty?: UserLoyalty;

  @computed
  public get currentTierKey() {
    return (this.userLoyalty?.currentTier?.key || 'unknown') as LoyaltyTierKey;
  }

  @action
  public setTutorialNextStep() {
    this.loyaltyProgramTutorial.step = this.loyaltyProgramTutorial.step + 1;
  }

  @action
  public startTutorial() {
    if (!this.userLoyalty?.currentTier) return;

    void analytics.sendEvent('loyalty_tutorial', {
      loyalty_club: this.userLoyalty.currentTier.key,
      loyalty_percentage: this.loyaltyPointsPercentages,
      tutorial_button_ind: 'impression',
    });

    this.loyaltyProgramTutorial.open = true;
  }

  @action
  public finishTutorial() {
    this.loyaltyProgramTutorial.step = LoyaltyTutorialSteps.CLOSED;

    void postRequest('user/profile', { finishedLoyaltyTutorial: true }).finally(() => {
      this.rootStore.userStore.updateProfile({ finishedLoyaltyTutorial: true });
    });

    this.loyaltyProgramTutorial.open = false;
    this.rootStore.featureManagerStore.onDialogFinished('LoyaltyProgram');
  }

  @action
  public setCompletedTierBonusEligibilityCheck(completed: boolean) {
    this.completedTierBonusEligibilityCheck = completed;
  }

  @action
  public setLoading(key: string, loading: boolean) {
    this.loading.set(key, loading);
  }

  @action
  private setAllTiers(tiers: Tier[]) {
    this.allTiers = tiers;
  }

  @action
  private setUserLoyalty(newUserLoyalty?: UserLoyalty) {
    this.userLoyalty = newUserLoyalty;
  }

  @computed
  public get loyaltyPointsPercentages() {
    if (!this.userLoyalty?.currentTier) return 0;

    const { currentTier, points } = this.userLoyalty ?? {};
    const progressPercentage = ((points || 0) / currentTier.pointsToComplete) * 100;
    return Math.floor(progressPercentage);
  }

  @computed
  public get isLoyaltyProgramDialogOpenWithTutorial() {
    return (
      this.loyaltyProgramTutorial.open &&
      [LoyaltyTutorialSteps.HOW_TO_EARN_POINTS, LoyaltyTutorialSteps.VIP_CLUB_BENEFITS].includes(
        this.loyaltyProgramTutorial.step,
      )
    );
  }

  @computed
  public get isTutorialOpen() {
    return this.loyaltyProgramTutorial.open;
  }

  @computed
  public get tutorialStep() {
    const { finishedLoyaltyTutorial } = this.rootStore.userStore.profile ?? {};

    if (finishedLoyaltyTutorial || !this.loyaltyProgramTutorialFlag) {
      return LoyaltyTutorialSteps.CLOSED;
    }

    return this.loyaltyProgramTutorial.step;
  }

  public updatePoints(newPoints: number) {
    if (this.userLoyalty) {
      this.setUserLoyalty({
        ...this.userLoyalty,
        points: newPoints,
      });
    }
  }

  public async fetchAllTiers() {
    try {
      this.setLoading('tiers', true);
      const response = await getRequest<ApiResponse<Tier[]>>('loyalty-program/tiers');
      this.setAllTiers(get(response, 'data.data', []));
    } finally {
      this.setLoading('tiers', false);
    }
  }

  public async fetchUserLoyalty() {
    try {
      if (!this.rootStore.authStore.authorized) return;

      this.setLoading('userLoyalty', true);
      const response = await getRequest<ApiResponse<UserLoyalty>>('loyalty-program/user-tier');
      const userLoyaltyData = get(response, 'data.data', undefined);
      this.setUserLoyalty(userLoyaltyData);
      return userLoyaltyData;
    } finally {
      this.setLoading('userLoyalty', false);
    }
  }

  public async claimTierEntryBonus() {
    try {
      this.setLoading('claimTierEntryBonus', true);
      await postRequest<ApiResponse<null>>('loyalty-program/claim-entry-bonus');
      void this.fetchUserLoyalty();
    } finally {
      this.setLoading('claimTierEntryBonus', false);
    }
  }

  public async claimTierMonthlyBonus() {
    try {
      this.setLoading('claimTierMonthlyBonus', true);
      await postRequest<ApiResponse<null>>('loyalty-program/claim-monthly-bonus');
      void this.fetchUserLoyalty();
    } finally {
      this.setLoading('claimTierMonthlyBonus', false);
    }
  }

  public async claimTierBirthdayGift() {
    try {
      this.setLoading('claimTierBirthdayGift', true);
      await postRequest<ApiResponse<null>>('loyalty-program/claim-birthday-gift');
      void this.fetchUserLoyalty();
    } finally {
      this.setLoading('claimTierBirthdayGift', false);
    }
  }

  public openNewTierCongratsDialog() {
    if (
      !this.rootStore.uiStore.openFullPageDialog &&
      !this.rootStore.uiStore.openDialog.dialog &&
      !this.rootStore.portalStore.isVisible
    ) {
      this.rootStore.uiStore.setOpenFullPageDialog('new_tier_congrats');
    }
  }

  public async refetchUserLoyalty() {
    if (!this.rootStore.authStore.authorized) return;

    this.setLoading('userLoyalty', true);
    const userLoyaltyData = await this.fetchUserLoyalty();

    const isCurrentTier = this.userLoyalty?.currentTier.UserTier?.isCurrentTier;
    const isEntryTier = this.userLoyalty?.currentTier.level === 1;
    const claimedEntryBonus = !!this.userLoyalty?.currentTier.UserTier?.entryDate;

    if (isCurrentTier && !isEntryTier && !claimedEntryBonus) {
      this.openNewTierCongratsDialog();
    }

    if (userLoyaltyData?.currentTier.UserTier && !userLoyaltyData.currentTier.UserTier.entryDate) {
      try {
        await Promise.all([this.rootStore.rewardsStore.getDailyBonus(), this.rootStore.appStore.fetchStorePackages()]);

        const ldContext = ldClient?.getContext();
        if (ldContext) {
          void identifyUser({
            ...ldContext,
            tier_key: userLoyaltyData.currentTier.key,
            tier_level: userLoyaltyData.currentTier.level,
          });
        }
      } catch (error) {
        devError('refetchUserLoyalty error', error);
      }
    }

    this.setLoading('userLoyalty', false);
  }

  private subscribeToSocket(socketData: UserLoyaltySocketMessage) {
    if (socketData.data.points) {
      this.updatePoints(socketData.data.points);
    }

    if (socketData.data.isNewTier) {
      void this.refetchUserLoyalty();
    }
  }
}
